import { memo, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { State } from "simple-markdown";

import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import { useFindingStore } from "stores/FindingStore";
import { SubmitState } from "stores/lib";

import useAnalytics from "effect/use_analytics";

import Charts from "./Charts";
import Table from "./Table";

// -----------------------------------------------------------------------------

const IAMFindingsPage = memo(function IAMFindingsPage(): JSX.Element {
  useAnalytics("Findings: IAM Findings");

  const [page, setPage] = useState(1);
  const [findingsStore, findingsActions] = useFindingStore();

  const isLoading =
    findingsStore.loadState === SubmitState.STARTED ||
    findingsStore.loadState === SubmitState.STARTED_AGAIN ||
    findingsStore.loadDeeplinkFindings === SubmitState.STARTED;

  useEffectOfLoadingData(
    page,
    setPage,
    findingsStore,
    findingsActions,
    useMemoOfURLSearchParams()
  );

  //
  return (
    <>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Reporting
      </Typography>
      <Grid container sx={{ display: "flex", flex: "1", gap: "24px" }}>
        <Grid item sx={{ display: "flex", flex: "1", flexDirection: "column" }}>
          <Table data={findingsStore.findings} isLoading={isLoading} />
        </Grid>
        <Charts
          findings={findingsStore.findings}
          loadingState={findingsStore.loadState}
        />
      </Grid>
    </>
  );
});

export default IAMFindingsPage;

// -----------------------------------------------------------------------------

function useMemoOfURLSearchParams() {
  const [searchParams] = useSearchParams();

  //
  return useMemo(() => searchParams, [searchParams]);
}

function useEffectOfLoadingData(
  page: number,
  setPage: Function,
  findingStore: State,
  findingsActions: any,
  params: URLSearchParams
) {
  const trigger = useMemo(() => {
    const triggerableParams = params;
    if (triggerableParams.get("page") === "0") {
      setPage(1);
    }

    triggerableParams.delete("finding");
    triggerableParams.delete("page");
    triggerableParams.set("tag", "iam");
    triggerableParams.sort();

    return triggerableParams.toString();
  }, [params, setPage]);

  const effect = () => {
    const newPage = params.get("page") === "0" ? 1 : page;
    params.set("page", String(newPage));
    findingsActions.load(newPage, params);
  };

  //
  useEffect(effect, [page, trigger, findingStore.loadDeeplinkFindings]); // eslint-disable-line
}
